<template>
  <div class="flex flex-col flex-auto bg-primary">
    <BlocksBlockRichText
      v-if="blogRichModuleData"
      :data="blogRichModuleData as RichTextModuleData"
    />
    <div id="list" />
    <PagesBlogCategories
      v-if="blogCategoriesData?.length"
      :categories-data="blogCategoriesData"
    />
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { RichTextModuleData } from "ecom-types";

const route = useRoute();
const { t } = useI18n();
const { $api } = useNuxtApp();
const generalStore = useGeneralStore();
const { visitEvent } = useEvents();
const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["page"],
  },
});

const { data: apiData } = await useAsyncData("blog-data", async () => {
  const [categoriesData, blogRichModule, pageData] = await Promise.all([
    $api.blog.getBlogCategories(),
    $api.blocks.getBlock("blog-rich-module"),
    $api.pages.getPage("blog"),
  ]);

  return {
    categoriesData,
    blogRichModule,
    pageData,
  };
});

const blogRichModuleData = computed(() => {
  return apiData.value?.blogRichModule?.data?.currentTranslation?.content;
});

const blogCategoriesData = computed(() => {
  return apiData.value?.categoriesData?.data;
});

const blogPageData = computed(() => {
  return apiData.value?.pageData?.data;
});

const activeBlogSection = computed(() =>
  blogCategoriesData.value?.find((el) => el.slug === route.params?.section),
);

const blogPageSchemaName = computed(() => {
  return (
    activeBlogSection.value?.currentTranslation?.title ||
    blogPageData.value?.currentTranslation?.title ||
    ""
  );
});

watchEffect(() => {
  const blogSectionSlug = route.params.section;
  const postSlug = route.params.slug; // need because component created before route change
  if (!activeBlogSection.value && blogSectionSlug && !postSlug) {
    throw createError({
      statusCode: 404,
      fatal: true,
    });
  }
});

const { seoTitle, seoDescription } = usePageSeo();
const { seoBlogTitle, seoBlogDescription } = useBlogPageSeo();

useHead(() => ({
  meta: [
    ...metaGenerator({
      title:
        seoBlogTitle.value ||
        blogPageData.value?.currentTranslation?.meta_title ||
        seoTitle.value,
      description:
        seoBlogDescription.value ||
        blogPageData.value?.currentTranslation?.meta_description ||
        seoDescription.value,
      image:
        activeBlogSection.value?.medias?.find(
          (el) => el.position === "open-graph",
        )?.file?.url ||
        blogPageData.value?.medias?.find((el) => el.position === "open-graph")
          ?.file?.url ||
        "",
      keywords:
        activeBlogSection.value?.currentTranslation?.meta_keywords ||
        blogPageData.value?.currentTranslation?.meta_keywords ||
        "",
      url: generalStore.bootstrap?.settings?.front_domain + route.path,
    }),
    ...(i18nHead.value.meta || []),
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
}));

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    seoBlogDescription.value ||
    blogPageData.value?.currentTranslation?.meta_description ||
    seoDescription.value,
  ogDescription: () =>
    seoBlogDescription.value ||
    blogPageData.value?.currentTranslation?.meta_description ||
    seoDescription.value,
});

onMounted(() => {
  visitEvent(blogPageData?.value?.is_plp);
});

function usePageSeo() {
  const seoTitle = computed(() => {
    let title =
      seoBlogTitle.value ||
      blogPageData.value?.currentTranslation?.meta_title ||
      blogPageData.value?.currentTranslation?.title ||
      "";
    if (generalStore.settings?.company_name) {
      title = `${title} | ${generalStore.settings.company_name}`;
    }
    return title;
  });

  const seoDescription = computed(() => {
    const title = blogPageData.value?.currentTranslation?.title || "";
    return `${title} ${t("pageMetaDescription")}`;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

function useBlogPageSeo() {
  const seoBlogTitle = computed(() => {
    if (activeBlogSection.value?.currentTranslation?.meta_title) {
      return activeBlogSection.value?.currentTranslation?.meta_title;
    }
    let title = activeBlogSection.value?.currentTranslation?.title || "";
    if (generalStore.settings?.company_name && title) {
      title = `${title} | ${generalStore.settings.company_name}`;
    }
    return title;
  });

  const seoBlogDescription = computed(() => {
    if (activeBlogSection.value?.currentTranslation?.meta_description) {
      return activeBlogSection.value?.currentTranslation?.meta_description;
    }

    const title = activeBlogSection.value?.currentTranslation?.title || "";
    if (!title) {
      return "";
    }
    return `${title} ${t("pageMetaDescription")}`;
  });
  return {
    seoBlogTitle,
    seoBlogDescription,
  };
}
provide(blogPageSchemaNameKey, blogPageSchemaName);
</script>
